import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing'); // processing | needs_tenant | error | done
  const [profile, setProfile] = useState(null);
  const [tenantName, setTenantName] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setStatus('error');
      return;
    }
    const sid = decodeURIComponent(match[1]);
    setSessionId(sid);

    (async () => {
      try {
        const { data } = await axios.post(
          `${BACKEND_URL}/api/auth/google/session`,
          { session_id: sid },
          { withCredentials: true }
        );
        // Clean fragment
        window.history.replaceState(null, '', window.location.pathname);
        if (data.needs_tenant_setup) {
          setProfile(data.profile);
          setStatus('needs_tenant');
          return;
        }
        await checkAuth();
        toast.success('Bem-vindo de volta!');
        const role = data?.user?.role;
        navigate(role === 'super_admin' ? '/platform' : '/dashboard');
      } catch (err) {
        const detail = err.response?.data?.detail || 'Falha ao autenticar com Google.';
        toast.error(detail);
        setStatus('error');
      }
    })();
  }, [checkAuth, navigate]);

  const submitTenant = async (e) => {
    e.preventDefault();
    if (!tenantName.trim()) {
      toast.error('Informe o nome da campanha.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/google/session`,
        { session_id: sessionId, tenant_name: tenantName.trim() },
        { withCredentials: true }
      );
      await checkAuth();
      toast.success('Conta criada! Seu trial de 7 dias começou.');
      navigate('/dashboard');
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao criar conta.');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto" />
          <p className="mt-4 text-slate-600 font-manrope">Autenticando com Google...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="auth-callback-error">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 px-6 text-center space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Não conseguimos entrar</h2>
            <p className="text-sm text-slate-600">
              Sua sessão Google expirou ou ocorreu um erro. Tente novamente.
            </p>
            <Button className="w-full" onClick={() => navigate('/login')} data-testid="auth-callback-back-login">
              Voltar para o login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // needs_tenant
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="auth-callback-needs-tenant">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 px-6 sm:px-8 space-y-5">
          <div className="text-center">
            {profile?.picture && (
              <img
                src={profile.picture}
                alt={profile?.name || ''}
                className="h-16 w-16 rounded-full mx-auto mb-3 object-cover"
              />
            )}
            <h2 className="text-2xl font-bold text-slate-900 font-outfit">
              Olá, {profile?.name?.split(' ')[0] || 'tudo bem'}!
            </h2>
            <p className="text-sm text-slate-600 mt-1 font-manrope">
              Última coisa: nos diga o nome da sua campanha.
            </p>
          </div>
          <form onSubmit={submitTenant} className="space-y-4">
            <div>
              <Label htmlFor="tenant_name">Nome da campanha</Label>
              <Input
                id="tenant_name"
                placeholder="Ex: Candidato João da Silva"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                data-testid="auth-callback-tenant-name"
                className="mt-1.5 h-11"
                autoFocus
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
              data-testid="auth-callback-submit-tenant"
            >
              {submitting ? 'Criando conta...' : 'Iniciar trial de 7 dias'}
            </Button>
          </form>
          <p className="text-xs text-slate-500 text-center font-manrope">
            Conectado como <strong>{profile?.email}</strong>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthCallback;
